<template>
  <a-modal
    :visible="visible"
    :title="$t('关联行政区域')"
    width="70%"
    :confirmLoading="confirmLoading"
    :maskClosable="false"
    @ok="handleOk"
    @cancel="handleCancel"
    :okText="$t('绑定')"
    :cancelText="$t('关闭')"
  >
    <a-spin :spinning="treeLoading || boundLoading">
      <a-alert type="info" showIcon style="margin-bottom: 10px">
        <div slot="message">
          {{ $t('当前GIS区域：') }}<span style="font-weight: bold">{{ tpAreaName || '-' }}</span>
          <span style="color: #999; margin-left: 10px">{{ $t('（仅支持绑定三级分区）') }}</span>
        </div>
      </a-alert>

      <a-row :gutter="16">
        <!-- 左侧：行政区域树 -->
        <a-col :span="12">
          <a-card size="small" :title="$t('行政区域（支持多选）')" :bordered="true" :bodyStyle="{ padding: '10px' }">
            <a-input-search
              v-model="searchAreaValue"
              style="margin-bottom: 8px"
              :placeholder="$t('请输入区域名称')"
              @search="onSearchArea"
              allowClear
            />
            <div class="tree-wrap">
              <el-tree
                ref="meterAreaTree"
                :data="areaTree"
                :props="defaultProps"
                node-key="key"
                show-checkbox
                :check-strictly="checkStrictly"
                :default-expand-all="false"
                :filter-node-method="filterNode"
                :highlight-current="true"
                @check="onCheck"
              >
                <span class="custom-tree-node" slot-scope="{ node, data }">
                  <span class="tree-node-title" :title="node.label">{{ node.label }}</span>
                </span>
              </el-tree>
            </div>
          </a-card>
        </a-col>

        <!-- 右侧：已绑定区域 -->
        <a-col :span="12">
          <a-card size="small" :title="$t('已绑定区域')" :bordered="true" :bodyStyle="{ padding: '10px' }">
            <a-spin :spinning="boundLoading">
              <a-empty v-if="!boundAreas.length" :description="$t('暂无绑定区域')" />
              <a-list v-else size="small" bordered :data-source="boundAreas" :split="true">
                <a-list-item slot="renderItem" slot-scope="item">
                  <a-list-item-meta :description="item.areaName">
                    <span slot="title">{{ item.areaNameFull || item.areaName }}</span>
                  </a-list-item-meta>
                  <a-popconfirm
                    :title="$t('确定要解绑该行政区域吗?')"
                    :ok-text="$t('确定')"
                    :cancel-text="$t('取消')"
                    @confirm="handleUnbind(item)"
                  >
                    <a-button
                      type="link"
                      style="color: #ff4d4f; padding: 0"
                      :loading="unbindLoadingId === item.id"
                    >
                      {{ $t('解绑') }}
                    </a-button>
                  </a-popconfirm>
                </a-list-item>
              </a-list>
            </a-spin>
          </a-card>
        </a-col>
      </a-row>
    </a-spin>
  </a-modal>
</template>

<script>
import { queryAreaTreeList, searchAreaByKeywords } from '@/api/api'
import { getAction, postAction } from '@/api/manage'

export default {
  name: 'BindMeterAreaModal',
  data() {
    return {
      visible: false,
      confirmLoading: false,
      treeLoading: false,
      boundLoading: false,
      unbindLoadingId: null,
      // 当前GIS区域
      tpAreaId: null,
      tpAreaName: '',
      // 行政区域树
      areaTree: [],
      searchAreaValue: '',
      defaultProps: {
        children: 'children',
        label: 'areaName',
      },
      checkStrictly: true,
      checkedKeys: [],
      // 已绑定区域
      boundAreas: [],
    }
  },
  watch: {
    searchAreaValue(val) {
      this.$refs.meterAreaTree && this.$refs.meterAreaTree.filter(val)
    },
  },
  methods: {
    /**
     * 打开弹框
     * @param record 当前选中的GIS区域对象（需包含 id 和 name）
     */
    show(record) {
      this.tpAreaId = record.id
      this.tpAreaName = record.name
      this.visible = true
      this.checkedKeys = []
      this.searchAreaValue = ''
      this.loadTree()
      this.loadBoundAreas()
    },
    // 加载行政区域树
    loadTree() {
      this.treeLoading = true
      queryAreaTreeList()
        .then(res => {
          if (res.success) {
            this.areaTree = res.result || []
          } else {
            this.$message.warning(res.message)
          }
        })
        .finally(() => {
          this.treeLoading = false
        })
    },
    onSearchArea(value) {
      this.searchAreaValue = value
    },
    filterNode(value, data) {
      if (!value) return true
      return (data.areaName || '').indexOf(value) !== -1
    },
    onCheck(data, { checkedKeys }) {
      this.checkedKeys = checkedKeys
    },
    // 加载已绑定区域（bindTpAreaId 为当前GIS区域id）
    loadBoundAreas() {
      if (!this.tpAreaId) {
        this.boundAreas = []
        return
      }
      this.boundLoading = true
      getAction('/jeecg-charge/meter/meterArea/list', {
        bindTpAreaId: this.tpAreaId,
        pageNo: 1,
        pageSize: 1000,
      })
        .then(res => {
          if (res.success) {
            // 分页对象 records 或直接数组
            this.boundAreas = (res.result && res.result.records) || res.result || []
          } else {
            this.$message.warning(res.message)
          }
        })
        .finally(() => {
          this.boundLoading = false
        })
    },
    // 绑定选中区域到当前GIS区域
    handleOk() {
      if (!this.checkedKeys.length) {
        this.$message.warning(this.$t('请选择要绑定的行政区域'))
        return
      }
      this.confirmLoading = true
      // 接口参数：tpAreaId=GIS区域id, AreaIds=行政区域id列表
      // 用 form 方式提交，保证后端 @RequestParam + List<String> 正确接收
      const formData = new FormData()
      formData.append('tpAreaId', this.tpAreaId)
      this.checkedKeys.forEach(id => formData.append('AreaIds', id))
      postAction('/jeecg-charge/meter/meterArea/meterBindTpArea', formData)
        .then(res => {
          if (res.success) {
            this.$message.success(res.message || this.$t('绑定成功'))
            this.loadBoundAreas()
            this.$emit('ok')
          } else {
            this.$message.error(res.message || this.$t('绑定失败'))
          }
        })
        .finally(() => {
          this.confirmLoading = false
        })
    },
    // 解绑单个行政区域
    handleUnbind(item) {
      this.unbindLoadingId = item.id
      const formData = new FormData()
      formData.append('id', item.id)
      postAction('/jeecg-charge/meter/meterArea/meterUnBindTpArea', formData)
        .then(res => {
          if (res.success) {
            this.$message.success(res.message || this.$t('解绑成功'))
            this.loadBoundAreas()
            this.$emit('ok')
          } else {
            this.$message.error(res.message || this.$t('解绑失败'))
          }
        })
        .finally(() => {
          this.unbindLoadingId = null
        })
    },
    handleCancel() {
      this.visible = false
    },
  },
}
</script>

<style scoped>
.tree-wrap {
  max-height: 55vh;
  overflow: auto;
}
.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding-right: 8px;
}
.tree-node-title {
  padding: 0 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.filter-tree.el-tree--highlight-current /deep/ .el-tree-node.is-checked > .el-tree-node__content {
  background: none;
}
.filter-tree.el-tree--highlight-current .el-tree-node.is-checked > .el-tree-node__content .tree-node-title {
  background-color: #bae7ff;
  padding: 0 5px;
}
</style>